import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { H1Title, Paragraph } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import SubmissionService from "../../services/submissionService";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheckCircle, faTimesCircle, faTrophy, faVideo, faExternalLinkAlt, faRocket } from "@fortawesome/free-solid-svg-icons";
import { getEmbedUrl } from "../../helpers/videoEmbed";

const SECTION_LABELS = {
  problemStatement: 'Problem Statement',
  proposedIntervention: 'Proposed Intervention',
  targetPopulation: 'Target Population',
  implementationPlan: 'Implementation Plan & Timeline',
};

export default function AdminSubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [eliminating, setEliminating] = useState(false);
  const [selectingWinner, setSelectingWinner] = useState(false);
  const [startingImpl, setStartingImpl] = useState(false);

  useEffect(() => { loadSubmission(); }, [id]);

  const loadSubmission = async () => {
    try {
      const { data } = await SubmissionService.getSubmission(id);
      setSubmission(data.data.submission);
    } catch (err) {
      ToastError('Failed to load submission');
    } finally { setLoading(false); }
  };

  const handleAdvance = async () => {
    setAdvancing(true);
    try {
      const roundMap = { round1: 'round2', round2: 'round3', round3: 'round4', round4: 'round5', round5: 'round6', round6: 'implementation' };
      const toRound = roundMap[submission.currentRound] || 'round2';
      const { data } = await SubmissionService.advance(id, { toRound });
      setSubmission(data.data.submission);
      Success('Submission advanced');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to advance');
    } finally { setAdvancing(false); }
  };

  const handleEliminate = async () => {
    setEliminating(true);
    try {
      const { data } = await SubmissionService.eliminate(id);
      setSubmission(data.data.submission);
      Success('Submission eliminated');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to eliminate');
    } finally { setEliminating(false); }
  };

  const handleSelectWinner = async () => {
    if (!confirm('Are you sure you want to select this submission as a winner?')) return;
    setSelectingWinner(true);
    try {
      const { data } = await SubmissionService.selectWinner(id);
      setSubmission(data.data.submission);
      Success('Winner selected!');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to select winner');
    } finally { setSelectingWinner(false); }
  };

  const handleStartImplementation = async () => {
    if (!confirm('Move this submission to the Implementation phase? The participant will gain access to the milestone tracker.')) return;
    setStartingImpl(true);
    try {
      const { data } = await SubmissionService.startImplementation(id);
      setSubmission(data.data.submission);
      Success('Implementation started — participant can now track milestones');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to start implementation');
    } finally { setStartingImpl(false); }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;
  if (!submission) return <div className="py-20 text-center text-grey-667">Submission not found</div>;

  const p = submission.proposal || {};
  const budget = p.budget || {};
  const outcome = p.anticipatedOutcome || {};

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/submissions')} className="text-grey-667 hover:text-primary cursor-pointer">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <H1Title size="xs">
              {submission.submitterId?.firstName} {submission.submitterId?.lastName}
            </H1Title>
            <Paragraph size="sm" className="text-grey-667">
              {submission.teamId?.name || 'Individual'} &middot; {submission.currentRound} &middot; {submission.status}
            </Paragraph>
          </div>
        </div>
        {submission.status !== 'eliminated' && submission.currentRound !== 'completed' && (
          <div className="flex gap-2">
            {!submission.isWinner && (
              <AppButton onClick={handleSelectWinner} loading={selectingWinner} fullWidth={false} size="sm" className="px-4 !bg-yellow-500 !text-black hover:!bg-yellow-600">
                <FontAwesomeIcon icon={faTrophy} className="mr-1" /> Select as Winner
              </AppButton>
            )}
            {submission.isWinner && (
              <span className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 text-yellow-600 rounded-lg text-sm font-bold">
                <FontAwesomeIcon icon={faTrophy} /> Winner
              </span>
            )}
            {submission.isWinner && submission.currentRound !== 'implementation' && (
              <AppButton onClick={handleStartImplementation} loading={startingImpl} fullWidth={false} size="sm" className="px-4 !bg-green !text-white hover:!bg-green-700">
                <FontAwesomeIcon icon={faRocket} className="mr-1" /> Start Implementation
              </AppButton>
            )}
            <AppButton onClick={handleAdvance} loading={advancing} fullWidth={false} size="sm" className="px-4">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Advance
            </AppButton>
            <AppButton onClick={handleEliminate} loading={eliminating} variant="danger" fullWidth={false} size="sm" className="px-4">
              <FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> Eliminate
            </AppButton>
          </div>
        )}
      </div>

      {/* Proposal Sections */}
      {Object.entries(SECTION_LABELS).map(([key, label]) => (
        <div key={key} className="bg-white rounded-xl border border-grey-200 p-5">
          <h3 className="font-bold text-sm text-grey-dark mb-2">{label}</h3>
          <p className="text-sm text-grey-667 whitespace-pre-wrap">{p[key] || <span className="italic text-grey-98">Not provided</span>}</p>
        </div>
      ))}

      {/* Budget */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <h3 className="font-bold text-sm text-grey-dark mb-3">Budget — Total: ${budget.totalAmount?.toLocaleString() || 0}</h3>
        {budget.lineItems?.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grey-200">
                <th className="text-left py-2 text-grey-dark">Item</th>
                <th className="text-right py-2 text-grey-dark">Amount</th>
                <th className="text-left py-2 pl-4 text-grey-dark">Justification</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-200">
              {budget.lineItems.map((item, i) => (
                <tr key={i}>
                  <td className="py-2 text-grey-667">{item.description || '-'}</td>
                  <td className="py-2 text-right">${(parseFloat(item.amount) || 0).toLocaleString()}</td>
                  <td className="py-2 pl-4 text-grey-667">{item.justification || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm italic text-grey-98">No budget items</p>
        )}
      </div>

      {/* Anticipated Outcome */}
      <div className="bg-white rounded-xl border border-grey-200 p-5 space-y-3">
        <h3 className="font-bold text-sm text-grey-dark">Anticipated Outcome</h3>
        <div>
          <p className="text-xs font-semibold text-grey-dark mb-1">Short-term</p>
          <p className="text-sm text-grey-667">{outcome.shortTerm || <span className="italic text-grey-98">Not provided</span>}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-grey-dark mb-1">Long-term</p>
          <p className="text-sm text-grey-667">{outcome.longTerm || <span className="italic text-grey-98">Not provided</span>}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-grey-dark mb-1">Problem Alignment</p>
          <p className="text-sm text-grey-667">{outcome.problemAlignment || <span className="italic text-grey-98">Not provided</span>}</p>
        </div>
      </div>

      {/* Supporting Documents */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <h3 className="font-bold text-sm text-grey-dark mb-2">Supporting Documents</h3>
        {p.supportingDocsUrl ? (
          <a href={p.supportingDocsUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm inline-flex items-center gap-1 break-all">
            <FontAwesomeIcon icon={faExternalLinkAlt} /> {p.supportingDocsUrl}
          </a>
        ) : (
          <p className="text-sm italic text-grey-98">Not provided</p>
        )}
      </div>

      {/* Video Pitch — show for any submission past round 1 so admins see "not submitted yet" */}
      {(submission.videoPitch?.url || !['round1', 'eliminated'].includes(submission.currentRound)) && (
        <div className="bg-white rounded-xl border border-grey-200 p-5 space-y-3">
          <h3 className="font-bold text-sm text-grey-dark">
            <FontAwesomeIcon icon={faVideo} className="mr-2 text-primary" />
            Round 2 — Video Pitch
          </h3>
          {submission.videoPitch?.url ? (
            <>
              {(() => {
                const embedUrl = getEmbedUrl(submission.videoPitch.url);
                return embedUrl ? (
                  <div className="rounded-xl overflow-hidden border border-grey-200 bg-black aspect-video">
                    <iframe src={embedUrl} className="w-full h-full" allowFullScreen allow="autoplay; encrypted-media" title="Video pitch" />
                  </div>
                ) : null;
              })()}
              <a href={submission.videoPitch.url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                <FontAwesomeIcon icon={faExternalLinkAlt} /> {submission.videoPitch.url}
              </a>
              {submission.videoPitch.submittedAt && (
                <p className="text-xs text-grey-98">Submitted {new Date(submission.videoPitch.submittedAt).toLocaleString()}</p>
              )}
            </>
          ) : (
            <p className="text-sm italic text-grey-98">Not submitted yet</p>
          )}
        </div>
      )}

      {/* Agreement Status */}
      {submission.participationAgreement?.signed && (
        <div className="bg-green-light rounded-xl border border-green/20 p-4 flex items-center gap-3">
          <FontAwesomeIcon icon={faCheckCircle} className="text-green" />
          <div>
            <p className="text-sm font-semibold text-green">Participation Agreement Signed</p>
            <p className="text-xs text-grey-667">
              Signed on {new Date(submission.participationAgreement.signedAt).toLocaleString()}
              {submission.participationAgreement.ipAddress && ` from ${submission.participationAgreement.ipAddress}`}
            </p>
          </div>
        </div>
      )}

      {/* Status History */}
      {submission.statusHistory?.length > 0 && (
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <h3 className="font-bold text-sm text-grey-dark mb-3">Status History</h3>
          <div className="space-y-2">
            {submission.statusHistory.map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="text-xs text-grey-98 w-32">{new Date(h.date).toLocaleDateString()}</span>
                <span className="text-grey-667">{h.round}: {h.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
